import { computed, watch, type MaybeRefOrGetter, toValue } from "vue";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type {
    AdminBanRulesListQueryParams,
    CreateBanRuleRequest,
    TestBanRuleRequest,
    UpdateBanRuleRequest,
} from "@shopexperts/legacy-api";

export const BAN_LIST_QUERY_KEY = ["admin", "ban-list"] as const;
export const BAN_RULES_QUERY_KEY = [...BAN_LIST_QUERY_KEY, "rules"] as const;

type BanRulesQuery = Omit<AdminBanRulesListQueryParams, "cursor">;

export function useBanRules(query: MaybeRefOrGetter<BanRulesQuery | undefined> = {}) {
    const api = useApi();
    const toaster = useToaster();

    const queryResult = useInfiniteQuery({
        queryKey: computed(() => [...BAN_RULES_QUERY_KEY, "list", toValue(query) ?? {}]),
        queryFn: ({ pageParam }) => api.admin.banList.rules.list({
            query: toValue(query),
            cursor: pageParam,
        }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.pagination.has_next
            ? lastPage.pagination.cursor ?? undefined
            : undefined,
        staleTime: 60_000,
        gcTime: 5 * 60_000,
    });

    watch(queryResult.error, (error) => {
        if (error) {
            toaster.create({ title: "Failed to load ban rules", type: "error" });
        }
    });

    return queryResult;
}

export function useBanRuleMutations() {
    const api = useApi();
    const queryClient = useQueryClient();
    const toaster = useToaster();

    /**
     * Invalidates the whole ban-list key: a rule edit changes what the email and
     * IP lists show (rule name, counts), and deleting one drops it from filters.
     */
    const invalidateBanList = () => {
        queryClient.invalidateQueries({ queryKey: [...BAN_LIST_QUERY_KEY] });
    };

    const createRule = useMutation({
        mutationFn: (body: CreateBanRuleRequest) => api.admin.banList.rules.create({ body }),
        onSuccess: () => {
            toaster.create({ title: "Ban rule created", type: "success" });
            invalidateBanList();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to create ban rule: ${error.message}`, type: "error" });
        },
    });

    const updateRule = useMutation({
        mutationFn: ({ ruleId, body }: { ruleId: number | string; body: UpdateBanRuleRequest }) => (
            api.admin.banList.rules.update({ params: { ruleId }, body })
        ),
        onSuccess: () => {
            toaster.create({ title: "Ban rule updated", type: "success" });
            invalidateBanList();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to update ban rule: ${error.message}`, type: "error" });
        },
    });

    /** Soft delete. Existing bans keep pointing at the rule. */
    const deleteRule = useMutation({
        mutationFn: (ruleId: number | string) => api.admin.banList.rules.delete({ params: { ruleId } }),
        onSuccess: () => {
            toaster.create({ title: "Ban rule deleted", type: "success" });
            invalidateBanList();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to delete ban rule: ${error.message}`, type: "error" });
        },
    });

    /** Dry run — nothing is banned, recorded, or counted, so no invalidation. */
    const testRules = useMutation({
        mutationFn: (body: TestBanRuleRequest) => api.admin.banList.rules.test({ body }),
        onError: (error: Error) => {
            toaster.create({ title: `Failed to run the test: ${error.message}`, type: "error" });
        },
    });

    return {
        createRule,
        updateRule,
        deleteRule,
        testRules,
    };
}
